import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { authActions } from '@ott/solar-auth';
import AuthModal from '@ott/solar-auth-modal';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';
import sendKM from '@ott/utility-km';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';
import { RootState } from 'src/redux/store';

import MobileAppBanner from 'src/components/blocks/MobileAppBanner';
import SupportChat from 'src/components/blocks/SupportChat';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import BookStaysCards from './blocks/BookStaysCards/BookStaysCards';
import ExploreStays from './blocks/ExploreStays/ExploreStays';
import FAQ from './blocks/FAQ';
import HowItWorks from './blocks/HowItWorks/HowItWorks';
import MainScreen from './blocks/MainScreen/MainScreen';
import SolarCashUsage from './blocks/SolarCashUsage/SolarCashUsage';
import { APP_LINK } from './constants';

import styles from './SolarCash.scss';

const SolarCashPage = () => {
  const auth = useTypedSelector(getAuthSelector);
  const ssr: boolean = useTypedSelector((state: RootState) => state.common.ssr);
  const isWebview = useIsWebview();
  const dispatch = useDispatch();
  const history = useHistory();
  const isAuthorized = auth?.data?.auth;

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  useEffect(() => {
    sendKM('SOLAR_CASH_PAGE_SHOW');

    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  if (ssr) {
    return null;
  }

  const renderMobileAppBlock = () => {
    const mobileAppTitle = (
      <div>
        Make it easier
        <br />
        <span className={styles.titleThin}>with the app</span>
      </div>
    );

    const mobileAppDescription = (
      <div>
        Elevate your travel experience by&nbsp;
        <br />
        installing the app today
      </div>
    );

    if (isWebview) {
      return null;
    }

    return (
      <MobileAppBanner
        title={mobileAppTitle}
        description={mobileAppDescription}
        picture="solar-coins"
        qrCodeLink={APP_LINK}
      />
    );
  };

  return (
    <>
      {isWebview ? (
        <AuthModal history={history} />
      ) : (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}
      <main className={styles.main}>
        <Container className={styles.container}>
          <MainScreen />
          <BookStaysCards />
          <HowItWorks />
          <SolarCashUsage />
          <ExploreStays />
          <FAQ />
          {renderMobileAppBlock()}
        </Container>
      </main>
      {!isWebview && <Footer />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </>
  );
};

export default SolarCashPage;
